import React, { HTMLAttributes, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import {
  UI_OPERATION_STATUS_REQUEST,
  nodeGet,
} from '@uncinc/react-drupal-core';

import {
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';

import { DEFAULT_LANGUAGE } from '../../../../config/constants';
import useClasses from '../../../../hooks/useClasses';
import useOperationStatus from '../../../../hooks/useOperationStatus';
import usePopup from '../../../../hooks/usePopup';
import useRoutes from '../../../../hooks/useRoutes';
import useToast from '../../../../hooks/useToast';
import useTypedDispatch from '../../../../hooks/useTypedDispatch';
import { nodeDelete, nodeEdit } from '../../../../store/actions/Entity/Node';
import { viewGet } from '../../../../store/actions/Entity/View';
import {
  Button,
  CoreLink,
  DiaryForm,
  type DairyEntryValues,
  Main,
  Popup,
} from '../../../../components';

import type { ImmutableDiaryNodeEntity } from '../..';

import './style.scss';

interface Props extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableDiaryNodeEntity;
}

const NodeDiaryFull: React.FC<Props> = ({ entity, className }) => {
  const { t } = useTranslation();
  const { diary } = useRoutes();
  const dispatch = useTypedDispatch();
  const history = useHistory();
  const toast = useToast();

  const deletePopup = usePopup('diary_delete_popup', {
    closeOnOutsideClick: true,
  });

  const nid = entity.getIn(['nid', 0, 'value']);
  const langcode = entity.getIn(['langcode', 0, 'value'], DEFAULT_LANGUAGE);
  const path = entity.getIn(['paths', langcode], '') as string;
  const title = entity.getIn(['title', 0, 'value'], '');
  const body = {
    value: entity.getIn(['field_diary_body', 0, 'value'], entity.getIn(['field_encrypted_body', 0, 'value'], entity.getIn(['body', 0, 'value'], ''))),
    processed: entity.getIn(['field_diary_body', 0, 'processed'], entity.getIn(['field_encrypted_body', 0, 'processed'], entity.getIn(['body', 0, 'processed'], ''))),
  };
  const entryDate = entity.getIn(['field_diary_entry_date', 0, 'value'], '');

  const operationStatusDelete = useOperationStatus('node', 'delete');

  const [edit, setEdit] = useState(false);
  const { buildClass, joinClasses } = useClasses('NodeDiary--Full', { edit });

  const handleEdit = (values: DairyEntryValues) => {
    // Temporary.
    // @TODO: Refactor this to make it work more globally.
    const editDiaryEntry = {
      type: [{ target_id: 'diary_entry' }],
      title: [{ value: values.title }],
      field_diary_body: [{ value: values.body, format: 'poor_text' }],
      field_diary_entry_date: [{ value: values.entryDate }],
    };

    // Wait for the node to be saved.
    dispatch(nodeEdit(nid, editDiaryEntry)).then(() => {
      // If the node has successfully been edited, navigate back to the node full page.
      dispatch(nodeGet(nid));
      setEdit(false);

      // Refresh the overview, else the node will not show up on the dairy entries page.
      dispatch(viewGet('diary_overview', 'default'));

      // Show success toast.
      toast.show(
        <Trans i18nKey="pages.diary.edit.edit_success_message" values={{ title }}>
          <strong />
        </Trans>,
        'success',
      );
    }).catch((error) => {
      if (error) {
        toast.show(
          <Trans i18nKey="pages.diary.edit.edit_error_message" values={{ title }}>
            <strong />
          </Trans>,
          'error',
        );
      }
    });
  };

  const handleDelete = () => {
    // Wait for the node to be deleted.
    dispatch(nodeDelete(nid)).then(() => {
      // Refresh the overview.
      dispatch(viewGet('diary_overview', 'block_1'));
    }).then(() => {
      // If the node has successfully been deleted, navigate back to the overview page,
      // which loads the diary view again.
      history.push(diary.overview);

      // Show success toast.
      toast.show(
        <Trans i18nKey="pages.diary.edit.delete_success_message" values={{ title }}>
          <strong />
        </Trans>,
        'success',
      );
    }).catch((error) => {
      if (error) {
        toast.show(
          <Trans i18nKey="pages.diary.edit.delete_error_message" values={{ title }}>
            <strong />
          </Trans>,
          'error',
        );
      }
    });
  };

  return (
    <div className="col-lg-8 py-8 py-lg-12">
      <article className={joinClasses(buildClass(), className)}>
        <header className={buildClass('header')}>
          <CoreLink
            to={!edit ? diary.overview : path}
            title={t('buttons.back')}
            className="icon-before icon-chevron icon-orientation-left"
            onClick={() => {
              if (edit) {
                setEdit(false);
              }
            }}
          />
        </header>

        <Main className={buildClass('content')}>
          <h1 className={joinClasses(buildClass('title'), 'u-word-break')}>
            {title}
          </h1>

          {edit ? (
            <DiaryForm
              initialValues={{ title, body: body.value, entryDate }}
              onSubmit={handleEdit}
              onDelete={() => deletePopup.open()}
            />
          ) : (
            <>
              <WysiwygField
                className={joinClasses(buildClass('body'), 'u-word-break')}
                property="schema:text"
                html={body.processed}
              />

              <Button secondary onClick={() => setEdit(true)}>
                {t('buttons.edit')}
              </Button>
            </>
          )}
        </Main>

        {deletePopup.isOpen && (
          <Popup
            title={t('pages.diary.edit.confirm_delete.title')}
            closeButtonText={t('pages.diary.edit.confirm_delete.cancel')}
            // eslint-disable-next-line react/no-unstable-nested-components
            cta={() => (
              <Button
                warning
                buttonState={operationStatusDelete === UI_OPERATION_STATUS_REQUEST && 'disabled'}
                onClick={handleDelete}
              >
                {t('pages.diary.edit.confirm_delete.confirm')}
              </Button>
            )}
            {...deletePopup.popupProps}
          >
            <p>
              <Trans i18nKey="pages.diary.edit.confirm_delete.body" values={{ title }}>
                <strong />
              </Trans>
            </p>
          </Popup>
        )}
      </article>
    </div>
  );
};

export default NodeDiaryFull;
